/**
 * AppSidebar - Floating glassmorphism sidebar shell
 * Desktop: fixed beside the page, collapsible to an icon rail.
 * Mobile: AppLayout renders it inside an off-canvas menu sheet instead.
 *
 * For designer userType, renders the editorial StudioSidebar instead.
 */

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { UserMenu } from "@/components/layout/user-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useSidebar } from "@/components/ui/sidebar";
import { useAuthStore } from "@/features/auth/store/auth.store";
import { getInitials } from "@/features/dashboard/utils/string-helpers";
import { RoleUnreadDot } from "@/features/roles/components/role-unread-dot";
import {
	useContractorProfile,
	useHomeownerProfile,
	useRealtorProfile,
} from "@/features/settings/hooks/use-profile";
import { StudioSidebar } from "@/features/studio/shell/studio-sidebar";
import { resolveMediaUrl } from "@/lib/utils";
import { getMainNav, isNavPathActive } from "./nav-config";

interface NavRowProps {
	label: string;
	icon: React.ReactNode;
	active: boolean;
	collapsed: boolean;
	onClick: () => void;
}

function NavRow({ label, icon, active, collapsed, onClick }: NavRowProps) {
	const [isHovered, setIsHovered] = useState(false);

	return (
		<button
			type="button"
			onClick={onClick}
			title={collapsed ? label : undefined}
			style={{
				display: "flex",
				alignItems: "center",
				gap: collapsed ? 0 : 10,
				justifyContent: collapsed ? "center" : "flex-start",
				padding: collapsed ? "10px 0" : "9px 10px",
				borderRadius: 8,
				border: active
					? "1px solid color-mix(in srgb, var(--color-dashboard-accent) 25%, transparent)"
					: "1px solid transparent",
				cursor: "pointer",
				fontFamily: active ? "var(--font-display)" : "var(--font-ui)",
				fontStyle: active ? "italic" : "normal",
				fontWeight: active ? 500 : 500,
				fontSize: active ? 15 : 13,
				color: active
					? "var(--color-dashboard-accent-dark)"
					: "var(--color-dashboard-muted-dark)",
				background: active
					? "linear-gradient(180deg, var(--color-dashboard-paper), var(--color-dashboard-divider))"
					: isHovered
						? "color-mix(in srgb, var(--color-dashboard-divider) 60%, transparent)"
						: "transparent",
				transition: "all 200ms cubic-bezier(0.22,1,0.36,1)",
				minHeight: 40,
				width: "100%",
				boxShadow: active
					? "0 1px 2px color-mix(in srgb, var(--color-dashboard-shadow-ink) 5%, transparent), 0 4px 12px -4px color-mix(in srgb, var(--color-dashboard-shadow-ink) 10%, transparent)"
					: "none",
			}}
			onMouseEnter={() => setIsHovered(true)}
			onMouseLeave={() => setIsHovered(false)}
		>
			<span
				style={{
					width: 20,
					height: 20,
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					flexShrink: 0,
				}}
			>
				{icon}
			</span>
			{!collapsed && (
				<span
					style={{
						whiteSpace: "nowrap",
						overflow: "hidden",
						textOverflow: "ellipsis",
					}}
				>
					{label}
				</span>
			)}
		</button>
	);
}

interface AppSidebarProps {
	/** Opens the designer's command palette, which AppLayout owns. */
	onOpenAssistant: () => void;
}

export function AppSidebar({ onOpenAssistant }: AppSidebarProps) {
	const { state, toggleSidebar, isMobile, setOpenMobile } = useSidebar();
	const { user } = useAuthStore();
	const { t } = useTranslation();
	const location = useLocation();
	const navigate = useNavigate();
	// On mobile AppLayout renders this inside the menu sheet, which always
	// shows it expanded and has no collapse control: the collapsed state
	// belongs to the desktop rail.
	const collapsed = !isMobile && state === "collapsed";
	const mainItems = getMainNav(user?.userType || undefined);
	// A query rather than a fetch in an effect: on mobile this component mounts
	// each time the menu sheet opens, and a remount within the query's
	// staleTime reads the cache instead of refetching.
	// Each role keeps its own name, on its own profile; only the active
	// role's profile is read. A designer's name is StudioSidebar's to read.
	const role = user?.userType;
	const { data: homeownerProfile } = useHomeownerProfile(role === "homeowner");
	const { data: contractorProfile } = useContractorProfile(
		role === "contractor",
	);
	const { data: realtorProfile } = useRealtorProfile(role === "realtor");
	const activeProfile =
		role === "homeowner"
			? homeownerProfile
			: role === "contractor"
				? contractorProfile
				: role === "realtor"
					? realtorProfile
					: null;
	const userName = activeProfile?.name ?? "";

	// Choosing an item closes the mobile menu sheet, including when the item
	// is the page already showing, which AppLayout's close-on-path-change
	// does not see.
	const closeMenu = () => setOpenMobile(false);

	// Render StudioSidebar for designer role
	if (user?.userType === "designer") {
		return (
			<StudioSidebar
				collapsed={collapsed}
				onToggleCollapse={isMobile ? undefined : toggleSidebar}
				onOpenAssistant={onOpenAssistant}
				onNavigate={closeMenu}
			/>
		);
	}

	const initials = userName
		? getInitials(userName)
		: (user?.userType?.charAt(0).toUpperCase() ?? "U");

	const isActive = (path: string) => isNavPathActive(location.pathname, path);

	const handleNav = (path: string) => {
		closeMenu();
		navigate(path);
	};

	const currentDate = new Date().toLocaleDateString("en-US", {
		weekday: "short",
		day: "numeric",
		month: "short",
		year: "numeric",
	});

	const ledgerNumber = "142";

	return (
		<aside
			style={{
				position: "fixed",
				top: 20,
				bottom: 20,
				left: 20,
				width: collapsed ? 62 : 198,
				borderRadius: 15,
				background:
					"linear-gradient(170deg, color-mix(in srgb, var(--color-dashboard-paper) 82%, transparent) 0%, color-mix(in srgb, var(--color-dashboard-surface) 78%, transparent) 55%, color-mix(in srgb, var(--color-dashboard-divider) 74%, transparent) 100%)",
				backdropFilter: "blur(28px) saturate(1.35)",
				WebkitBackdropFilter: "blur(28px) saturate(1.35)",
				border:
					"1px solid color-mix(in srgb, var(--color-dashboard-border) 85%, transparent)",
				boxShadow: [
					"inset 0 1px 0 color-mix(in srgb, var(--color-dashboard-paper) 65%, transparent)",
					"inset 0 -1px 0 color-mix(in srgb, var(--color-border-default) 45%, transparent)",
					"inset 1px 0 0 color-mix(in srgb, var(--color-dashboard-paper) 35%, transparent)",
					"0 1px 2px color-mix(in srgb, var(--color-dashboard-shadow-ink) 4%, transparent)",
					"0 12px 28px -8px color-mix(in srgb, var(--color-dashboard-shadow-ink) 14%, transparent)",
					"0 32px 64px -16px color-mix(in srgb, var(--color-dashboard-shadow-ink) 18%, transparent)",
					"0 48px 100px -28px color-mix(in srgb, var(--color-dashboard-shadow-ink) 22%, transparent)",
				].join(", "),
				zIndex: 50,
				display: "flex",
				flexDirection: "column",
				padding: collapsed ? "18px 9px" : "20px 12px",
				transition:
					"width 420ms cubic-bezier(0.22,1,0.36,1), padding 420ms cubic-bezier(0.22,1,0.36,1)",
				overflow: "hidden",
			}}
		>
			<div
				style={{
					position: "absolute",
					top: -50,
					right: -50,
					width: 160,
					height: 160,
					borderRadius: "50%",
					background:
						"radial-gradient(circle, color-mix(in srgb, var(--color-dashboard-accent-light) 10%, transparent) 0%, transparent 65%)",
					pointerEvents: "none",
				}}
			/>

			<div
				style={{
					display: "flex",
					alignItems: "center",
					justifyContent: collapsed ? "center" : "space-between",
					minHeight: 36,
					position: "relative",
				}}
			>
				<button
					type="button"
					onClick={() => handleNav("/home")}
					style={{
						background: "transparent",
						border: 0,
						cursor: "pointer",
						display: "flex",
						alignItems: "baseline",
						justifyContent: collapsed ? "center" : "flex-start",
						padding: collapsed ? "2px 4px 0" : "4px 4px 0",
						marginLeft: collapsed ? -4 : 0,
						fontFamily: "var(--font-display)",
						fontWeight: 700,
						fontSize: collapsed ? 26 : 24,
						letterSpacing: "-0.02em",
						color: "var(--color-dashboard-text)",
						minHeight: 36,
						zIndex: 1,
					}}
				>
					{collapsed ? (
						<span
							style={{
								fontFamily: "var(--font-display)",
								fontStyle: "italic",
								fontWeight: 500,
								fontSize: 28,
								color: "var(--color-dashboard-accent-dark)",
								marginLeft: 2,
							}}
						>
							f.
						</span>
					) : (
						<>
							finish
							<span
								style={{
									fontFamily: "var(--font-display)",
									fontStyle: "italic",
									fontWeight: 500,
									color: "var(--color-dashboard-accent-dark)",
									marginLeft: -1,
								}}
							>
								d.
							</span>
						</>
					)}
				</button>

				{isMobile ? null : !collapsed ? (
					<button
						type="button"
						onClick={toggleSidebar}
						style={{
							position: "absolute",
							right: 4,
							width: 26,
							height: 26,
							borderRadius: "50%",
							background:
								"linear-gradient(var(--color-dashboard-paper), var(--color-dashboard-surface))",
							border:
								"1px solid color-mix(in srgb, var(--color-border-default) 85%, transparent)",
							boxShadow:
								"color-mix(in srgb, var(--color-dashboard-paper) 85%, transparent) 0px 1px 0px inset, color-mix(in srgb, var(--color-dashboard-shadow-ink) 10%, transparent) 0px 1px 2px, color-mix(in srgb, var(--color-dashboard-shadow-ink) 20%, transparent) 0px 4px 12px -4px",
							cursor: "pointer",
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							color: "var(--color-dashboard-muted-dark)",
							transition: "200ms",
							zIndex: 2,
						}}
					>
						<svg
							width="12"
							height="12"
							viewBox="0 0 24 24"
							fill="none"
							stroke="currentColor"
							strokeWidth="2"
							strokeLinecap="round"
							strokeLinejoin="round"
							aria-label="Collapse sidebar"
						>
							<path d="m15 18-6-6 6-6" />
						</svg>
					</button>
				) : (
					<button
						type="button"
						onClick={toggleSidebar}
						style={{
							width: 20,
							height: 20,
							borderRadius: "50%",
							background:
								"linear-gradient(var(--color-dashboard-paper), var(--color-dashboard-surface))",
							border:
								"1px solid color-mix(in srgb, var(--color-border-default) 85%, transparent)",
							boxShadow:
								"color-mix(in srgb, var(--color-dashboard-paper) 85%, transparent) 0px 1px 0px inset, color-mix(in srgb, var(--color-dashboard-shadow-ink) 10%, transparent) 0px 1px 2px, color-mix(in srgb, var(--color-dashboard-shadow-ink) 20%, transparent) 0px 4px 12px -4px",
							cursor: "pointer",
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							color: "var(--color-dashboard-muted-dark)",
							transition: "200ms",
							zIndex: 2,
							marginLeft: 2,
						}}
					>
						<svg
							width="10"
							height="10"
							viewBox="0 0 24 24"
							fill="none"
							stroke="currentColor"
							strokeWidth="2"
							strokeLinecap="round"
							strokeLinejoin="round"
							aria-label="Expand sidebar"
						>
							<path d="m9 18 6-6-6-6" />
						</svg>
					</button>
				)}
			</div>

			{!collapsed && (
				<div
					style={{
						padding: "8px 4px 14px",
						margin: "10px 0 4px",
						position: "relative",
					}}
				>
					<div
						style={{
							fontFamily: "var(--font-mono)",
							fontSize: 8.5,
							fontWeight: 600,
							letterSpacing: "0.24em",
							textTransform: "uppercase",
							color: "var(--color-dashboard-accent-dark)",
						}}
					>
						No. {ledgerNumber}
					</div>
					<div
						style={{
							fontFamily: "var(--font-display)",
							fontStyle: "italic",
							fontSize: 12,
							color: "var(--color-dashboard-muted-dark)",
							marginTop: 2,
						}}
					>
						{currentDate}
					</div>
					<div
						style={{
							position: "absolute",
							left: 4,
							right: 4,
							bottom: 0,
							height: 1,
							background:
								"linear-gradient(90deg, transparent, color-mix(in srgb, var(--color-border-default) 70%, transparent), transparent)",
						}}
					/>
				</div>
			)}
			{collapsed && (
				<div
					style={{
						margin: "12px 8px 6px",
						height: 1,
						background:
							"linear-gradient(90deg, transparent, color-mix(in srgb, var(--color-border-default) 60%, transparent), transparent)",
					}}
				/>
			)}

			<nav
				style={{
					marginTop: collapsed ? 6 : 10,
					display: "flex",
					flexDirection: "column",
					gap: 2,
				}}
			>
				{mainItems.map((item) => (
					<NavRow
						key={item.path}
						label={item.labelKey ? t(item.labelKey) : item.label}
						icon={<item.icon size={20} />}
						active={isActive(item.path)}
						collapsed={collapsed}
						onClick={() => handleNav(item.path)}
					/>
				))}
			</nav>

			<div style={{ flex: 1, minHeight: 16 }} />

			<div
				style={{
					marginTop: collapsed ? 6 : 10,
					display: "flex",
					flexDirection: "column",
					gap: 2,
				}}
			>
				<UserMenu name={userName} onNavigate={closeMenu}>
					<button
						type="button"
						style={{
							display: "flex",
							alignItems: "center",
							justifyContent: collapsed ? "center" : "flex-start",
							padding: collapsed ? "10px 0" : "9px 10px",
							borderRadius: 8,
							border: isActive("/settings")
								? "1px solid color-mix(in srgb, var(--color-dashboard-accent) 25%, transparent)"
								: "1px solid transparent",
							cursor: "pointer",
							background: isActive("/settings")
								? "linear-gradient(180deg, var(--color-dashboard-paper), var(--color-dashboard-divider))"
								: "transparent",
							transition: "all 200ms cubic-bezier(0.22,1,0.36,1)",
							minHeight: 30,
							maxHeight: 45,
							width: "100%",
							boxShadow: isActive("/settings")
								? "0 1px 2px color-mix(in srgb, var(--color-dashboard-shadow-ink) 5%, transparent), 0 4px 12px -4px color-mix(in srgb, var(--color-dashboard-shadow-ink) 10%, transparent)"
								: "none",
						}}
						onMouseEnter={(e) => {
							if (!isActive("/settings")) {
								e.currentTarget.style.background =
									"color-mix(in srgb, var(--color-dashboard-divider) 60%, transparent)";
							}
						}}
						onMouseLeave={(e) => {
							if (!isActive("/settings")) {
								e.currentTarget.style.background = "transparent";
							}
						}}
					>
						<span className="relative inline-flex shrink-0">
							<Avatar className="h-8 w-8" style={{ borderRadius: 13 }}>
								<AvatarImage
									src={resolveMediaUrl(user?.profilePictureUrl)}
									alt={user?.userType ?? "User"}
								/>
								<AvatarFallback
									style={{
										borderRadius: 8,
										background:
											"linear-gradient(135deg, color-mix(in srgb, var(--color-dashboard-accent) 12%, transparent) 0%, color-mix(in srgb, var(--color-dashboard-accent) 8%, transparent) 100%)",
										color: "var(--color-dashboard-accent-dark)",
										fontWeight: 600,
									}}
								>
									{initials}
								</AvatarFallback>
							</Avatar>
							<RoleUnreadDot className="-right-0.5 -top-0.5" />
						</span>
						{!collapsed && (
							<span
								style={{
									marginLeft: 10,
									whiteSpace: "nowrap",
									overflow: "hidden",
									textOverflow: "ellipsis",
									fontFamily: isActive("/settings")
										? "var(--font-display)"
										: "var(--font-ui)",
									fontStyle: isActive("/settings") ? "italic" : "normal",
									fontSize: isActive("/settings") ? 15 : 13,
									fontWeight: 500,
									color: isActive("/settings")
										? "var(--color-dashboard-accent-dark)"
										: "var(--color-dashboard-muted-dark)",
								}}
							>
								{userName || t("userMenu.account")}
							</span>
						)}
					</button>
				</UserMenu>
			</div>
		</aside>
	);
}
